import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import { modulesData, moduleGroups } from '../../data/modulesData';

const ModulesOverviewPage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Catálogo de módulos asisant',
    itemListElement: modulesData.map((m, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: m.name,
      url: `https://asisant.com/modulos/${m.slug}`,
      description: m.tagline,
    })),
  };

  return (
    <div className="min-h-screen bg-white" data-testid="modules-overview-page">
      <SEO
        title="Catálogo de módulos · Cloud, IT, partners y gobernanza"
        description="Descubre los 25 módulos de asisant: CSP & Billing, ITAM/SAM, Starlink, ITSM, GRC, FinOps, CRM, IA y más. Plataforma modular: activa solo lo que necesitas y escala a tu ritmo."
        keywords="asisant, módulos, plataforma cloud, CSP, ITAM, SAM, ITSM, GRC, FinOps, Starlink, CRM, IA"
        canonical="/modulos"
        structuredData={structuredData}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-dark-ellipse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="inline-block px-4 py-2 bg-white/10 text-white/90 rounded-full text-sm font-medium mb-6 backdrop-blur-sm border border-white/20">
            Catálogo completo
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Todos los módulos <span style={{ color: '#4E95D9' }}>asisant</span>
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-8">
            Una plataforma modular para gestionar partners, cloud, IT, personas, riesgos, finanzas e innovación. Activa solo lo que necesitas y escala a tu ritmo.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {moduleGroups.map((g) => (
              <a
                key={g.name}
                href={`#group-${g.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                className="px-4 py-2 bg-white/10 text-white/90 rounded-full text-sm font-medium backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors"
                data-testid={`group-pill-${g.name}`}
              >
                {g.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES BY GROUP */}
      {moduleGroups.map((group) => {
        const items = modulesData.filter((m) => m.group === group.name);
        const groupId = `group-${group.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;
        return (
          <section key={group.name} id={groupId} className="py-20 odd:bg-white even:bg-[#f7f9fc] scroll-mt-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-12">
                <span className="inline-block px-4 py-2 bg-[#4E95D9]/10 text-[#2B6FA8] rounded-full text-sm font-medium mb-4">
                  {group.description}
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {group.name}
                </h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((m) => {
                  const Icon = LucideIcons[m.icon] || LucideIcons.Box;
                  return (
                    <Link
                      key={m.slug}
                      to={`/modulos/${m.slug}`}
                      className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#4E95D9] transition-all duration-300 hover:shadow-xl hover:shadow-[#4E95D9]/10 hover:-translate-y-1"
                      data-testid={`module-card-${m.slug}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg"
                          style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#4E95D9] group-hover:translate-x-1 transition-all" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#2B6FA8] transition-colors">
                        {m.name}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{m.tagline}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* FINAL CTA */}
      <section className="py-24 bg-dark-ellipse relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿No sabes por dónde empezar?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Cuéntanos tu operación y te armamos un paquete de módulos con ROI claro.
          </p>
          <Link to="/contacto?source=modules">
            <Button
              size="lg"
              className="bg-white hover:bg-white/90 text-[#2B6FA8] font-semibold px-8 py-6 rounded-full transition-all hover:scale-105"
              data-testid="modules-overview-cta"
            >
              Hablar con un experto
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ModulesOverviewPage;
